import React, { useCallback } from 'react'

import { useGetFormData } from 'cng-common/features/form'
import { IFormDropdownProps } from 'cng-common/features/form/components/formDropdown/models'
import { IFormInputProps } from 'cng-common/features/form/components/formInput/models'
import { isValidEmail } from 'core-components/library/validation'
import { Static } from 'core-components/types'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import { Crud } from 'core/types/Crud'
import { IPartnerTerritory } from 'onboarding/classes/models/partnerTerritoryModels'
import { OnboardingFormInput } from 'onboarding/components/ui/theme'
import {
  FormLocationTreeAdapter,
  OnboardingFormDropdownAdapter,
  OnboardingFormInputAdapter,
} from 'onboarding/components/ui/visualAudienceAdapters'
import { CHINA_COUNTRY_ISO } from 'onboarding/components/widgets/public/request/contract/constants'

import { TFormLocationTreeExtended } from '../formLocationTreeExtended/FormLocationTreeExtended'
import { TDropdownsCruds, TRequestorFillForm, TStaticCrudItem } from '../models'
import styles from '../RequestorFill.module.scss'

interface IProps {
  formKey: string
  requestCountryISO: string
  partnerTerritory: IPartnerTerritory
  cruds: TDropdownsCruds
  hpSigningEntity: string | null
  territorySale: string[]
  category: string | null
  zyme: string | null
  bigDealEnablement: string | null
  territoryOfSaleRoot?: string
  reason: string | null
  writeAudience: IAudienceCondition
  isDisabled?: boolean
}

const getZymeSource = () =>
  new Crud({
    fetcher: new Static([
      { label: 'Yes', key: 'Yes' },
      { label: 'No', key: 'No' },
    ]),
    keyField: 'key',
  })

export const FormFields = ({
  formKey,
  requestCountryISO,
  partnerTerritory,
  cruds,
  hpSigningEntity,
  territorySale,
  zyme,
  category,
  reason,
  territoryOfSaleRoot,
  bigDealEnablement,
  writeAudience,
  isDisabled,
}: IProps) => {
  const [{ formData }, handleChange] = useGetFormData<TRequestorFillForm>(formKey)
  const isZymeNo = formData.ZYME === 'No'
  const resetReason = useCallback(() => {
    handleChange({
      field: 'Reason',
      value: '',
    })
  }, [handleChange])

  return (
    <div>
      <div className={styles.wrapper}>
        <VisualAudienceExtended<IFormDropdownProps<any>>
          write={writeAudience}
          formKey={formKey}
          component={OnboardingFormDropdownAdapter}
          label={'HP Signing Entity'}
          field={'HPSigningEntity'}
          source={cruds.hpSigningEntityApj}
          defaultValue={hpSigningEntity || ''}
          caption={'en'}
          isDisabled={isDisabled}
          isRequired
        />
      </div>

      <div className={styles.wrapper}>
        <VisualAudienceExtended<TFormLocationTreeExtended>
          write={writeAudience}
          formKey={formKey}
          partnerTerritory={partnerTerritory}
          component={FormLocationTreeAdapter}
          label={'Territory of Sale'}
          defaultValue={territorySale}
          field={'TerritorySaleISO'}
          root={territoryOfSaleRoot}
          isDisabled={isDisabled}
          isRequired
        />
      </div>

      <div className={styles.wrapper}>
        <OnboardingFormInput
          formKey={formKey}
          field={'Category'}
          label={'Category'}
          defaultValue={category || 'Administration'}
          isDisabled
        />
      </div>

      <div className={isZymeNo ? styles.wrapper : undefined}>
        <VisualAudienceExtended<IFormDropdownProps<TStaticCrudItem>>
          write={writeAudience}
          formKey={formKey}
          component={OnboardingFormDropdownAdapter}
          label={'Does the T1 partner have ERP system to submit ZYME data?'}
          field={'ZYME'}
          source={getZymeSource()}
          defaultValue={zyme || ''}
          caption={'label'}
          onChange={resetReason}
          isDisabled={isDisabled}
          isStatic
          isRequired
        />
      </div>

      <div className={styles.wrapper}>
        <VisualAudienceExtended<IFormInputProps<string>>
          component={OnboardingFormInputAdapter}
          write={writeAudience}
          formKey={formKey}
          label={`If 'No', what is the planned timeframe for installing the system`}
          field={'Reason'}
          defaultValue={reason || ''}
          isVisible={isZymeNo}
          isDisabled={isDisabled}
          isRequired
        />
      </div>

      <VisualAudienceExtended<IFormDropdownProps<any>>
        write={writeAudience}
        formKey={formKey}
        component={OnboardingFormDropdownAdapter}
        label={'China Big Deal enablement'}
        field={'BigDealEnablement'}
        source={cruds.bigDealEnablement}
        defaultValue={bigDealEnablement || ''}
        caption={'en'}
        isDisabled={isDisabled}
        isVisible={requestCountryISO === CHINA_COUNTRY_ISO}
        isRequired
      />
    </div>
  )
}
